import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Baby, Plus, Edit } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BirthRecord = ({ baby }) => {
  const navigate = useNavigate();
  const viewOnlyMode = true; // Always view-only in baby app, only doctors can edit from their dashboard
  const [birthRecord, setBirthRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [doctorInfo, setDoctorInfo] = useState(null);
  const [formData, setFormData] = useState({
    sex: '',
    mode_of_delivery: '',
    birth_weight: '',
    length: '',
    head_circumference: '',
    blood_group: '',
    mother_name: '',
    father_name: '',
    family_history: '',
    allergies: '',
    remarks: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchBirthRecord();
    // Get doctor info from localStorage if available
    try {
      const storedDoctor = localStorage.getItem('doctor');
      const storedHospital = localStorage.getItem('hospital');
      if (storedDoctor && storedHospital) {
        setDoctorInfo({
          doctor: JSON.parse(storedDoctor),
          hospital: JSON.parse(storedHospital)
        });
      }
    } catch (error) {
      console.error('Error loading doctor info from localStorage:', error);
    }
  }, [baby]);

  const fetchBirthRecord = async () => {
    try {
      const response = await axios.get(`${API}/birth-record/${baby.id}`);
      if (response.data) {
        setBirthRecord(response.data);
      }
    } catch (error) {
      console.error('Error fetching birth record:', error);
      if (error.response?.status !== 404) {
        toast.error('Failed to load birth record');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.sex || !formData.mode_of_delivery || !formData.birth_weight) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const recordData = {
        baby_id: baby.id,
        sex: formData.sex,
        mode_of_delivery: formData.mode_of_delivery,
        birth_weight: parseFloat(formData.birth_weight),
        length: formData.length ? parseFloat(formData.length) : null,
        head_circumference: formData.head_circumference ? parseFloat(formData.head_circumference) : null,
        blood_group: formData.blood_group || null,
        mother_name: formData.mother_name || null,
        father_name: formData.father_name || null,
        family_history: formData.family_history || null,
        allergies: formData.allergies || null,
        remarks: formData.remarks || null
      };

      // Add doctor information if available
      if (doctorInfo) {
        recordData.updated_by = doctorInfo.doctor.id;
        recordData.updated_by_doctor = `${doctorInfo.doctor.name}${doctorInfo.doctor.specialization ? ' (' + doctorInfo.doctor.specialization + ')' : ''}`;
        recordData.updated_by_hospital = doctorInfo.hospital.name;
      }
      
      if (birthRecord) {
        await axios.put(`${API}/birth-record/${birthRecord.id}`, recordData);
        toast.success('Birth record updated successfully!');
      } else {
        await axios.post(`${API}/birth-record`, recordData);
        toast.success('Birth record created successfully!');
      }
      
      // Always update baby's mother_name and father_name to ensure sync (including removal)
      const babyUpdates = {
        mother_name: formData.mother_name && formData.mother_name.trim() ? formData.mother_name.trim() : null,
        father_name: formData.father_name && formData.father_name.trim() ? formData.father_name.trim() : null
      };
      
      // Also update blood_group if present
      if (formData.blood_group) {
        babyUpdates.blood_type = formData.blood_group;
      }
      
      const response = await axios.put(`${API}/babies/${baby.id}`, babyUpdates);
      
      // Update localStorage with the new baby data
      const updatedBaby = response.data;
      localStorage.setItem('currentBaby', JSON.stringify(updatedBaby));
      
      setShowDialog(false);
      fetchBirthRecord();
      
      toast.success('Birth record and baby profile updated!');
      
      // Reload after a delay to ensure localStorage is saved and user sees the toast
      setTimeout(() => {
        window.location.reload();
      }, 1500);
      
    } catch (error) {
      console.error('Error saving birth record:', error);
      toast.error('Failed to save birth record');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditDialog = () => {
    // Helper function to normalize blood group (add + if missing)
    const normalizeBloodGroup = (bg) => {
      if (!bg) return '';
      // If blood group doesn't have + or -, add + by default
      if (!bg.includes('+') && !bg.includes('-')) {
        return `${bg}+`;
      }
      return bg;
    };
    
    if (birthRecord) {
      setFormData({
        sex: birthRecord.sex || '',
        mode_of_delivery: birthRecord.mode_of_delivery || '',
        birth_weight: birthRecord.birth_weight?.toString() || '',
        length: birthRecord.length?.toString() || '',
        head_circumference: birthRecord.head_circumference?.toString() || '',
        blood_group: normalizeBloodGroup(birthRecord.blood_group),
        mother_name: birthRecord.mother_name || '',
        father_name: birthRecord.father_name || '',
        family_history: birthRecord.family_history || '',
        allergies: birthRecord.allergies || '',
        remarks: birthRecord.remarks || ''
      });
    } else {
      setFormData({
        sex: baby.gender || '',
        mode_of_delivery: '',
        birth_weight: baby.weight?.toString() || '',
        length: baby.height?.toString() || '',
        head_circumference: '',
        blood_group: normalizeBloodGroup(baby.blood_type),
        mother_name: '',
        father_name: '',
        family_history: '',
        allergies: '',
        remarks: ''
      });
    }
    setShowDialog(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading birth record...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="dashboard-header">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/')}
              className="text-white hover:bg-white hover:bg-opacity-20"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                <Baby className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold" style={{fontFamily: 'Space Grotesk'}}>Birth Record</h1>
                <p className="text-blue-200 text-sm">Birth details and medical history of {baby.name}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 py-6">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900" style={{fontFamily: 'Space Grotesk'}}>
              Birth Information
            </h3>
            {!viewOnlyMode && (
            <Button 
              onClick={openEditDialog}
              className="bg-gray-900 hover:bg-gray-800 text-white"
            >
              {birthRecord ? (
                <>
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Record
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Record
                </>
              )}
            </Button>
            )}
          </div>
          
          <div className="p-6">
            {birthRecord ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="border-b pb-3">
                    <h4 className="text-sm font-medium text-gray-500 mb-1">Sex</h4>
                    <p className="text-base font-semibold text-gray-900">{birthRecord.sex}</p>
                  </div>
                  
                  <div className="border-b pb-3">
                    <h4 className="text-sm font-medium text-gray-500 mb-1">Mode of Delivery</h4>
                    <p className="text-base font-semibold text-gray-900">{birthRecord.mode_of_delivery}</p>
                  </div>
                  
                  <div className="border-b pb-3">
                    <h4 className="text-sm font-medium text-gray-500 mb-1">Birth Weight</h4>
                    <p className="text-base font-semibold text-gray-900">{birthRecord.birth_weight} kg</p>
                  </div>
                  
                  <div className="border-b pb-3">
                    <h4 className="text-sm font-medium text-gray-500 mb-1">Length</h4>
                    <p className="text-base font-semibold text-gray-900">
                      {birthRecord.length ? `${birthRecord.length} cm` : 'Not recorded'}
                    </p>
                  </div>
                  
                  <div className="border-b pb-3">
                    <h4 className="text-sm font-medium text-gray-500 mb-1">Head Circumference</h4>
                    <p className="text-base font-semibold text-gray-900">
                      {birthRecord.head_circumference ? `${birthRecord.head_circumference} cm` : 'Not recorded'}
                    </p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="border-b pb-3">
                    <h4 className="text-sm font-medium text-gray-500 mb-1">Blood Group</h4>
                    <p className="text-base font-semibold text-gray-900">
                      {birthRecord.blood_group ? (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-red-100 text-red-800">
                          {birthRecord.blood_group.includes('+') || birthRecord.blood_group.includes('-') 
                            ? birthRecord.blood_group 
                            : `${birthRecord.blood_group}+`}
                        </span>
                      ) : 'Not recorded'}
                    </p>
                  </div>
                  
                  <div className="border-b pb-3">
                    <h4 className="text-sm font-medium text-gray-500 mb-1">Family History</h4>
                    <p className="text-sm text-gray-700">
                      {birthRecord.family_history || 'No family history recorded'}
                    </p>
                  </div>
                  
                  <div className="border-b pb-3">
                    <h4 className="text-sm font-medium text-gray-500 mb-1">Allergies</h4>
                    <p className="text-sm text-gray-700">
                      {birthRecord.allergies || 'No allergies recorded'}
                    </p>
                  </div>
                  
                  <div className="border-b pb-3">
                    <h4 className="text-sm font-medium text-gray-500 mb-1">Remarks</h4>
                    <p className="text-sm text-gray-700">
                      {birthRecord.remarks || 'No remarks'}
                    </p>
                  </div>
                </div>

                {/* Updated By Section - spans both columns */}
                {birthRecord.updated_by_doctor && (
                  <div className="col-span-1 md:col-span-2 mt-2 pt-4 border-t border-gray-200">
                    <p className="text-xs text-gray-500">
                      Updated by <span className="font-medium text-gray-700">{birthRecord.updated_by_doctor}</span>
                      {birthRecord.updated_by_hospital && (
                        <>, {birthRecord.updated_by_hospital}</>
                      )}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <Baby className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-lg font-medium">No birth record found</p>
                <p className="text-sm mt-1">Birth record will be added by your doctor</p>
              </div>
            )}
          </div>
        </div>

        {/* Birth Record Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="sm:max-w-2xl z-50 max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{birthRecord ? 'Edit Birth Record' : 'Add Birth Record'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="sex">Sex *</Label>
                  <Select onValueChange={(value) => setFormData(prev => ({ ...prev, sex: value }))} value={formData.sex}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select sex" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Male">Male</SelectItem>
                      <SelectItem value="Female">Female</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="mode_of_delivery">Mode of Delivery *</Label>
                  <Select onValueChange={(value) => setFormData(prev => ({ ...prev, mode_of_delivery: value }))} value={formData.mode_of_delivery}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select delivery mode" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Normal Vaginal Delivery">Normal Vaginal Delivery</SelectItem>
                      <SelectItem value="Cesarean Section">Cesarean Section</SelectItem>
                      <SelectItem value="Vacuum Extraction">Vacuum Extraction</SelectItem>
                      <SelectItem value="Forceps Delivery">Forceps Delivery</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="birth_weight">Birth Weight (kg) *</Label>
                  <Input
                    id="birth_weight"
                    type="number"
                    step="0.01"
                    placeholder="3.5"
                    value={formData.birth_weight}
                    onChange={(e) => setFormData(prev => ({ ...prev, birth_weight: e.target.value }))}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="length">Length (cm)</Label>
                  <Input
                    id="length"
                    type="number"
                    step="0.1"
                    placeholder="50.0"
                    value={formData.length}
                    onChange={(e) => setFormData(prev => ({ ...prev, length: e.target.value }))}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="head_circumference">Head Circumference (cm)</Label>
                  <Input
                    id="head_circumference"
                    type="number"
                    step="0.1"
                    placeholder="35.0"
                    value={formData.head_circumference}
                    onChange={(e) => setFormData(prev => ({ ...prev, head_circumference: e.target.value }))}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="blood_group">Blood Group</Label>
                <Select onValueChange={(value) => setFormData(prev => ({ ...prev, blood_group: value }))} value={formData.blood_group}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select blood group" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="A+">A+</SelectItem>
                    <SelectItem value="A-">A-</SelectItem>
                    <SelectItem value="B+">B+</SelectItem>
                    <SelectItem value="B-">B-</SelectItem>
                    <SelectItem value="AB+">AB+</SelectItem>
                    <SelectItem value="AB-">AB-</SelectItem>
                    <SelectItem value="O+">O+</SelectItem>
                    <SelectItem value="O-">O-</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="mother_name">Mother's Name</Label>
                  <Input
                    id="mother_name"
                    type="text"
                    placeholder="Enter mother's name"
                    value={formData.mother_name}
                    onChange={(e) => {
                      const value = e.target.value.split(' ').map(word => 
                        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                      ).join(' ');
                      setFormData(prev => ({ ...prev, mother_name: value }));
                    }}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="father_name">Father's Name</Label>
                  <Input
                    id="father_name"
                    type="text"
                    placeholder="Enter father's name"
                    value={formData.father_name}
                    onChange={(e) => {
                      const value = e.target.value.split(' ').map(word => 
                        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                      ).join(' ');
                      setFormData(prev => ({ ...prev, father_name: value }));
                    }}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="family_history">Family History</Label>
                <Textarea
                  id="family_history"
                  placeholder="Enter any relevant family medical history..."
                  value={formData.family_history}
                  onChange={(e) => setFormData(prev => ({ ...prev, family_history: e.target.value }))}
                  rows={3}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="allergies">Allergies</Label>
                <Textarea
                  id="allergies"
                  placeholder="Enter any known allergies..."
                  value={formData.allergies}
                  onChange={(e) => setFormData(prev => ({ ...prev, allergies: e.target.value }))}
                  rows={2}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="remarks">Remarks</Label>
                <Textarea
                  id="remarks"
                  placeholder="Any additional notes or remarks..."
                  value={formData.remarks}
                  onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
                  rows={3}
                />
              </div>
              
              <div className="flex space-x-2 pt-4">
                <Button 
                  type="submit" 
                  className="flex-1"
                  disabled={submitting}
                >
                  {submitting ? 'Saving...' : 'Save Record'}
                </Button>
                <Button 
                  type="button"
                  variant="outline" 
                  onClick={() => setShowDialog(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default BirthRecord;
